import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { Card, CardBody, CardHeader } from 'reactstrap';
import { getPaginasBloqueadas } from '../../../api/reportApi';
import TableContainer from '../../../components/TableContainerReactTable';
import SkeletonCard from './SkeletonCard';
import styles from './ReportCard.module.scss';
import numeral from 'numeral';

interface PageBlockedCardProps {
  campaignId: string;
}

export interface PageBlockedCardRef {
  refresh: () => void;
}

const PageBlockedCard = forwardRef<PageBlockedCardRef, PageBlockedCardProps>(
  ({ campaignId }, ref) => {
    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    numeral.locale('pt-br');
    const numeralInteger = '0,0';

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const pageData = await getPaginasBloqueadas(parseInt(campaignId));
        setData(pageData);
      } catch (err) {
        console.error('Error fetching blocked pages data:', err);
        setError('Erro ao carregar dados de páginas bloqueadas');
      } finally {
        setLoading(false);
      }
    };

    useEffect(() => {
      if (campaignId) {
        fetchData();
      }
    }, [campaignId]);

    useImperativeHandle(ref, () => ({
      refresh: fetchData
    }));

    if (loading) {
      return <SkeletonCard rows={2} columns={2} />;
    }

    if (error) {
      return (
        <Card className={styles.reportCard}>
          <CardHeader className={styles.cardHeader}>
            <h4 className={styles.cardTitle}>Páginas bloqueadas</h4>
          </CardHeader>
          <CardBody>
            <div className="text-center text-danger">
              <i className="ri-error-warning-line me-2"></i>
              {error}
            </div>
          </CardBody>
        </Card>
      );
    }

    if (!data || data.length === 0) {
      return (
        <Card className={styles.reportCard}>
          <CardHeader className={styles.cardHeader}>
            <h4 className={styles.cardTitle}>Páginas bloqueadas</h4>
          </CardHeader>
          <CardBody>
            <div className="text-center text-muted">
              <i className="ri-information-line me-2"></i>
              Nenhuma página bloqueada encontrada
            </div>
          </CardBody>
        </Card>
      );
    }

    // Column definitions for the table
    const columns = [
      {
        header: 'Página',
        accessorFn: (row: any) => row.originHostname + row.originPath,
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        id: 'impression',
        header: 'Impressões',
        accessorFn: (row: any) => row.impression || 0 + (row.start || 0),
        cell: (row: any) => numeral(row.getValue() || 0).format(numeralInteger),
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        id: 'blocked',
        header: 'Bloqueadas',
        accessorFn: (row: any) => row.blocked || 0,
        cell: (row: any) => numeral(row.getValue() || 0).format(numeralInteger),
        enableColumnFilter: false,
        enableSorting: true,
      },
    ];

    return (
      <Card className={styles.reportCard}>
        <CardHeader className={styles.cardHeader}>
          <h4 className={styles.cardTitle}>Páginas bloqueadas</h4>
        </CardHeader>
        <CardBody>
          <TableContainer
            divClass="table-responsive"
            columns={columns}
            data={data}
            isGlobalFilter={false}
            customPageSize={5}
            initialSorting={[{ id: 'blocked', desc: true }]}
          />
        </CardBody>
      </Card>
    );
  }
);

PageBlockedCard.displayName = 'PageBlockedCard';

export default PageBlockedCard;
